import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "@/components/common/Reveal";
import {
    MapPin,
    HeartHandshake,
    TrendingUp,
    Trees,
    Zap,
    ArrowRight,
} from "lucide-react";

const STEPS = ["Observer", "Comprendre", "Révéler", "Concevoir", "Transmettre"];

const PILLARS = [
    {
        num: "01",
        icon: MapPin,
        title: "Le Lieu",
        subtitle: "Lire le terrain",
        img: "/images/barn-exterior.jpg",
        intro:
            "Tout commence par une lecture stratégique du lieu. Avant toute décision, nous analysons votre terrain, votre bâti ou votre site dans ses dimensions visibles et invisibles.",
        sub: [
            {
                label: "Le visible",
                text: "Notre conception bioclimatique optimise vos bâtiments agricoles : l'orientation du lieu, la topographie, les contraintes climatiques.",
            },
            {
                label: "L'invisible",
                text: "La géobiologie appliquée, qui s'appuie sur le ressenti, à la manière des anciens bâtisseurs qui ne laissaient rien au hasard. Nous cherchons les réseaux telluriques, veines d'eau, organisation des flux et perturbations électromagnétiques qui peuvent altérer votre lieu et votre bien-être, parfois quantifiables et mesurables.",
            },
        ],
        outro:
            "Cette lecture conditionne toutes les décisions qui suivent. Un terrain mal compris génère des contraintes durables ; un lieu bien lu révèle des potentiels énergétiques et structurels qu'aucun plan standard n'aurait identifiés.",
    },
    {
        num: "02",
        icon: HeartHandshake,
        title: "Le Vivant",
        subtitle: "Placer l'homme et l'animal au coeur du projet",
        img: "/images/cattle.jpg",
        intro:
            "Nous concevons des espaces pensés pour ceux qui les utilisent au quotidien. Une fois le lieu compris, nous élaborons la stratégie globale : implantation, orientation, organisation des espaces, structuration des flux intérieurs et extérieurs, choix des matériaux naturels et biosourcés, cohérence bioclimatique et énergétique.",
        sub: [
            {
                label: "Une cohérence d'ensemble",
                text: "Chaque décision est pensée en fonction de votre lieu, de vos usages et du bien-être de l'animal comme de ceux qui l'exploitent. L'objectif reste le même : un espace harmonisé, ventilé, performant et durable.",
            },
            {
                label: "Matériaux biosourcés",
                text: "Nos matériaux biosourcés servent la performance énergétique et thermique du bâti, ainsi que la santé de l'homme et de l'animal. Nos structures évoluent avec vous et votre élevage.",
            },
        ],
        outro:
            "Rozier Concept intervient en conception globale et en assistance à maîtrise d'ouvrage, en collaboration avec les professionnels habilités si besoin : architectes, maîtres d'oeuvre, bureaux d'études. Notre rôle est de porter la cohérence d'ensemble et de garantir que chaque dimension parle le même langage.",
    },
    {
        num: "03",
        icon: TrendingUp,
        title: "La Performance",
        subtitle: "Optimiser les flux et les investissements",
        img: "/images/equestrian2.jpg",
        intro:
            "Nous recherchons l'efficacité fonctionnelle, la réduction des déplacements, l'évolutivité, la rentabilité à long terme et la performance zootechnique.",
        outro:
            "Chaque décision doit avoir un sens pratique et rester conforme aux normes en vigueur.",
    },
    {
        num: "04",
        icon: Trees,
        title: "Le Paysage",
        subtitle: "Intégrer plutôt qu'imposer",
        img: "/images/pasture.jpg",
        intro:
            "Nous sommes paysagistes. Nous veillons à l'intégration visuelle, à la préservation des vues, à la qualité des aménagements extérieurs et à la valorisation du patrimoine rural.",
        outro: "Le paysage devient un véritable partenaire du projet.",
    },
    {
        num: "05",
        icon: Zap,
        title: "L'Énergie",
        subtitle: "Préparer l'exploitation de demain",
        img: "/images/solar1.jpg",
        intro:
            "Nous étudions avec vos partenaires le photovoltaïque, la gestion de l'eau et les ressources naturelles du site.",
        outro:
            "L'objectif est de créer des exploitations plus autonomes et résilientes.",
    },
];

const Hero = () => (
    <section
        data-testid="methode-hero"
        className="relative flex min-h-[78svh] items-end overflow-hidden"
    >
        <div className="absolute inset-0">
            <img
                src="/images/methode-hero.jpg"
                alt="Paysage rural au coeur d'une exploitation"
                className="h-full w-full object-cover rz-hero-zoom"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-rz-deep/60 via-rz-deep/45 to-rz-deep/85" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-12 pb-16 md:pb-24 pt-32">
            <motion.span
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="text-xs md:text-sm tracking-[0.25em] uppercase text-rz-pale"
            >
                La Méthode Rozier®
            </motion.span>
            <motion.h1
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
                className="font-display mt-5 max-w-4xl text-white font-extrabold tracking-tight leading-[1] text-[clamp(2.4rem,6vw,5rem)] [text-shadow:_0_2px_30px_rgba(10,26,18,0.45)]"
            >
                L'intelligence du lieu au service de la performance.
            </motion.h1>
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="mt-9 flex flex-wrap gap-2.5"
            >
                {STEPS.map((s) => (
                    <span
                        key={s}
                        data-testid={`methode-step-${s}`}
                        className="rounded-full border border-white/25 bg-white/5 px-4 py-2 text-xs md:text-sm font-medium text-white backdrop-blur-sm"
                    >
                        {s}
                    </span>
                ))}
            </motion.div>
        </div>
    </section>
);

const Approche = () => (
    <section
        data-testid="methode-approche"
        className="bg-rz-bg py-24 lg:py-32 px-6 md:px-12"
    >
        <div className="mx-auto max-w-[1400px] grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-6">
                <Reveal>
                    <span className="text-xs tracking-[0.22em] uppercase text-rz-sage font-semibold">
                        Une approche globale au service du vivant
                    </span>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="font-display mt-5 text-[clamp(1.9rem,4vw,3.2rem)] font-bold tracking-tight leading-[1.08] text-rz-ink">
                        Des lieux qui produisent, protègent et transmettent.
                    </h2>
                </Reveal>
            </div>
            <div className="lg:col-span-6">
                <Reveal delay={0.2}>
                    <p className="text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                        La Méthode Rozier® se distingue par sa capacité à faire dialoguer des
                        disciplines qui travaillent rarement ensemble : l'architecture,
                        l'agriculture, le paysage, l'énergie et l'analyse du lieu.
                    </p>
                    <p className="mt-5 text-base lg:text-lg leading-relaxed text-rz-ink-soft">
                        Cette transversalité permet de concevoir des projets cohérents, durables
                        et à forte valeur ajoutée.
                    </p>
                </Reveal>
            </div>
        </div>
    </section>
);

const Pillar = ({ p, index }) => {
    const reversed = index % 2 === 1;
    return (
        <div
            data-testid={`methode-pillar-${p.num}`}
            className="grid gap-10 lg:grid-cols-12 lg:gap-16 items-center"
        >
            <div className={`lg:col-span-6 ${reversed ? "lg:order-2" : ""}`}>
                <Reveal>
                    <div className="overflow-hidden rounded-[2rem]">
                        <img
                            src={p.img}
                            alt={p.title}
                            className="h-full w-full object-cover aspect-[4/3] transition-transform [transition-duration:1200ms] hover:scale-105"
                        />
                    </div>
                </Reveal>
            </div>
            <div className={`lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
                <Reveal>
                    <div className="flex items-center gap-4">
                        <span className="font-display text-5xl font-extrabold text-rz-pale">
                            {p.num}
                        </span>
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rz-green text-white">
                            <p.icon size={20} />
                        </span>
                    </div>
                </Reveal>
                <Reveal delay={0.08}>
                    <h3 className="font-display mt-5 text-[clamp(1.8rem,3.5vw,2.6rem)] font-bold tracking-tight text-rz-ink">
                        {p.title}
                    </h3>
                    <p className="mt-2 text-rz-sage font-medium italic">{p.subtitle}</p>
                </Reveal>
                <Reveal delay={0.14}>
                    <p className="mt-6 text-base leading-relaxed text-rz-ink-soft">
                        {p.intro}
                    </p>
                </Reveal>
                {p.sub && (
                    <div className="mt-6 space-y-4">
                        {p.sub.map((s, i) => (
                            <Reveal key={s.label} delay={0.1 + i * 0.06}>
                                <div className="rounded-xl border-l-2 border-rz-sage bg-white p-5">
                                    <p className="text-xs tracking-[0.16em] uppercase text-rz-green font-semibold">
                                        {s.label}
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-rz-ink-soft">
                                        {s.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                )}
                {p.outro && (
                    <Reveal delay={0.18}>
                        <p className="mt-6 text-base leading-relaxed text-rz-ink-soft">
                            {p.outro}
                        </p>
                    </Reveal>
                )}
            </div>
        </div>
    );
};

const Pillars = () => (
    <section
        data-testid="methode-pillars"
        className="bg-white py-24 lg:py-32 px-6 md:px-12"
    >
        <div className="mx-auto max-w-[1400px]">
            <div className="max-w-2xl mb-16 lg:mb-24">
                <Reveal>
                    <span className="text-xs tracking-[0.22em] uppercase text-rz-sage font-semibold">
                        Les 5 piliers
                    </span>
                </Reveal>
                <Reveal delay={0.1}>
                    <h2 className="font-display mt-5 text-[clamp(2rem,4.5vw,3.4rem)] font-bold tracking-tight leading-[1.05] text-rz-ink">
                        Une méthode structurée, du terrain à l'énergie.
                    </h2>
                </Reveal>
            </div>
            <div className="space-y-20 lg:space-y-32">
                {PILLARS.map((p, i) => (
                    <Pillar key={p.num} p={p} index={i} />
                ))}
            </div>
        </div>
    </section>
);

const Engagement = () => (
    <section
        data-testid="methode-engagement"
        className="relative overflow-hidden bg-rz-deep px-6 md:px-12 py-20 lg:py-28"
    >
        <div
            className="absolute inset-0 opacity-20"
            style={{
                backgroundImage: "url('/images/equestrian1.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
        />
        <div className="absolute inset-0 bg-rz-deep/70" />
        <div className="relative z-10 mx-auto max-w-[1400px] text-center">
            <Reveal>
                <span className="text-xs tracking-[0.22em] uppercase text-rz-sage font-semibold">
                    Notre engagement
                </span>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="font-display mx-auto mt-5 max-w-4xl text-[clamp(2rem,5vw,3.6rem)] font-extrabold tracking-tight leading-[1.05] text-white">
                    Bâtir des lieux uniques, vibrants et durables.
                </h2>
            </Reveal>
            <Reveal delay={0.2}>
                <Link
                    to="/contact"
                    data-testid="methode-engagement-cta"
                    className="group mt-10 inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-rz-green transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/30"
                >
                    Démarrer votre projet
                    <ArrowRight
                        size={18}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                </Link>
            </Reveal>
        </div>
    </section>
);

export default function Methode() {
    return (
        <main data-testid="methode-page">
            <Hero />
            <Approche />
            <Pillars />
            <Engagement />
        </main>
    );
}
